/**
 * @deprecated This context is deprecated and will be removed in a future version.
 * Use the TanStack Query hooks from '@/hooks/quiz' instead:
 * - useStartSession() for starting a quiz session
 * - useActiveQuizSession() for accessing session state
 * - useSubmitAnswer() for submitting answers
 * - useNavigateBack() for back navigation
 * - useQuizProgress() for progress calculation
 * - useQuizCompletion() for completion handling
 *
 * Migration completed: December 2025
 * See: QuizContainer.tsx for the new implementation
 */

/**
 * Quiz Context
 *
 * Provides centralized state management for quiz sessions including:
 * - Session initialization and state
 * - Current question and navigation
 * - Answer submission and validation
 * - Progress tracking
 * - Session persistence
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import type {
  SessionState,
  CurrentQuestion,
  AnswerValue,
} from '@quiz-commerce/quiz-engine';
import { quizService } from '../services/api';
import type { ApiError } from '../services/api';

/**
 * Quiz progress tracking
 */
export interface QuizProgress {
  currentQuestionIndex: number;
  totalQuestions: number;
  completedQuestions: number;
  percentComplete: number;
}

/**
 * Quiz context value
 */
export interface QuizContextValue {
  // Session state
  sessionId: string | null;
  tenantId: string;
  sessionState: SessionState | null;

  // Question state
  currentQuestion: CurrentQuestion | null;
  previousQuestions: CurrentQuestion[];

  // Answers
  answers: Record<string, AnswerValue>;

  // Progress
  progress: QuizProgress;

  // UI state
  loading: boolean;
  error: ApiError | null;

  // Actions
  initializeSession: (tenantId: string, quizConfigId?: string) => Promise<void>;
  submitAnswer: (questionKey: string, answer: AnswerValue) => Promise<void>;
  navigateBack: () => Promise<void>;
  clearError: () => void;
  abandonSession: () => Promise<void>;
}

/**
 * Storage keys
 */
const STORAGE_PREFIX = 'quiz_session';

/**
 * Get storage key for tenant
 */
const getStorageKey = (tenantId: string) => `${STORAGE_PREFIX}_${tenantId}`;

/**
 * Type guard to check if error is ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as ApiError).code === 'string' &&
    typeof (error as ApiError).message === 'string'
  );
}

/**
 * Convert unknown error to ApiError
 */
function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  };
}

/**
 * Persisted session data
 */
interface PersistedSessionData {
  sessionId: string;
  tenantId: string;
  currentQuestionIndex: number;
  answers: Record<string, AnswerValue>;
  timestamp: number;
}

/**
 * Quiz Context
 */
const QuizContext = createContext<QuizContextValue | undefined>(undefined);

/**
 * Quiz Provider Props
 */
export interface QuizProviderProps {
  children: ReactNode;
  tenantId: string;
  quizConfigId?: string;
  autoInitialize?: boolean;
}

/**
 * Transform QuestionDto (backend) to CurrentQuestion (quiz-engine format)
 */
function transformQuestionDtoToCurrentQuestion(questionDto: any): CurrentQuestion {
  // Validate required fields
  if (!questionDto) {
    console.error('[Transform] ERROR: questionDto is null/undefined!');
    throw new Error('Cannot transform null/undefined questionDto');
  }
  if (!questionDto.questionKey) {
    console.error('[Transform] ERROR: Missing questionKey!', questionDto);
    throw new Error('Missing required field: questionKey');
  }
  if (!questionDto.questionType) {
    console.error('[Transform] ERROR: Missing questionType!', questionDto);
    throw new Error('Missing required field: questionType');
  }
  if (!questionDto.questionText) {
    console.warn('[Transform] WARNING: Missing questionText!', questionDto);
  }

  const result = {
    question: {
      questionKey: questionDto.questionKey,
      questionType: questionDto.questionType as CurrentQuestion['question']['questionType'],
      questionText: questionDto.questionText,
      displayOrder: questionDto.questionNumber,
      helpText: questionDto.helpText,
      options: questionDto.options,
      validation: questionDto.validation,
    },
    questionNumber: questionDto.questionNumber,
    totalQuestions: questionDto.totalQuestions,
    isFirst: questionDto.isFirst,
    isLast: questionDto.isLast,
    canGoBack: questionDto.canGoBack,
  };

  return result;
}

/**
 * Quiz Provider Component
 */
/** @deprecated Use QuizContainer with TanStack Query hooks instead */
export const QuizProvider: React.FC<QuizProviderProps> = ({
  children,
  tenantId,
  quizConfigId,
  autoInitialize = false,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<CurrentQuestion | null>(null);
  const [previousQuestions, setPreviousQuestions] = useState<CurrentQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Mounted guard to prevent setState on unmounted component
   * This prevents the K.current crash during async operations
   */
  const isMountedRef = useRef(true);

  /**
   * Cleanup effect to mark component as unmounted
   */
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Safe setState wrappers that check if component is still mounted
   * Prevents "K.current" crash from setState on unmounted component
   */
  const safeSetLoading = useCallback((value: boolean) => {
    if (!isMountedRef.current) {
      return;
    }
    setLoading(value);
  }, []);

  const safeSetError = useCallback((value: ApiError | null) => {
    if (!isMountedRef.current) {
      return;
    }
    setError(value);
  }, []);

  const safeSetSessionId = useCallback((value: string | null) => {
    if (!isMountedRef.current) {
      return;
    }
    setSessionId(value);
  }, []);

  const safeSetSessionState = useCallback((value: SessionState | null) => {
    if (!isMountedRef.current) {
      return;
    }
    setSessionState(value);
  }, []);

  const safeSetCurrentQuestion = useCallback((value: CurrentQuestion | null) => {
    if (!isMountedRef.current) {
      return;
    }
    setCurrentQuestion(value);
  }, []);

  const safeSetAnswers = useCallback((value: Record<string, AnswerValue>) => {
    if (!isMountedRef.current) {
      return;
    }
    setAnswers(value);
  }, []);

  const safeSetPreviousQuestions = useCallback((updater: (prev: CurrentQuestion[]) => CurrentQuestion[]) => {
    if (!isMountedRef.current) {
      return;
    }
    setPreviousQuestions(updater);
  }, []);

  /**
   * Calculate progress
   */
  const progress: QuizProgress = React.useMemo(() => {
    if (!currentQuestion) {
      return {
        currentQuestionIndex: 0,
        totalQuestions: 0,
        completedQuestions: 0,
        percentComplete: 0,
      };
    }

    const completedQuestions = currentQuestion.questionNumber - 1;
    const percentComplete = (completedQuestions / currentQuestion.totalQuestions) * 100;

    return {
      currentQuestionIndex: currentQuestion.questionNumber,
      totalQuestions: currentQuestion.totalQuestions,
      completedQuestions,
      percentComplete,
    };
  }, [currentQuestion]);

  /**
   * Persist session to localStorage
   */
  const persistSession = useCallback(
    (data: Partial<PersistedSessionData>) => {
      if (!sessionId || !tenantId) return;

      const existingData = localStorage.getItem(getStorageKey(tenantId));
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- JSON.parse returns 'any', safely merged with PersistedSessionData structure
      const existing = existingData ? JSON.parse(existingData) : {};

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Object spread safely types the result as PersistedSessionData
      const persistedData: PersistedSessionData = {
        sessionId,
        tenantId,
        currentQuestionIndex: currentQuestion?.questionNumber || 0,
        answers,
        timestamp: Date.now(),
        ...existing,
        ...data,
      };

      localStorage.setItem(getStorageKey(tenantId), JSON.stringify(persistedData));
    },
    [sessionId, tenantId, currentQuestion, answers]
  );

  /**
   * Clear session from localStorage
   */
  const clearSession = useCallback(() => {
    localStorage.removeItem(getStorageKey(tenantId));
  }, [tenantId]);

  /**
   * Restore session from localStorage
   */
  const restoreSession = useCallback((): PersistedSessionData | null => {
    const stored = localStorage.getItem(getStorageKey(tenantId));
    if (!stored) return null;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- JSON.parse returns 'any', validated through timestamp checks below
    const data: PersistedSessionData = JSON.parse(stored);

    // Check if session expired (24 hours)
    const expirationTime = 24 * 60 * 60 * 1000; // 24 hours
    if (Date.now() - data.timestamp > expirationTime) {
      clearSession();
      return null;
    }

    return data;
  }, [tenantId, clearSession]);

  /**
   * Initialize quiz session
   *
   * === STABLE FUNCTION REFERENCE USING useRef ===
   * This prevents the complex dependency chain that causes Oe.current crash
   */
  const initializeSessionRef = useRef<((tenant: string, configId?: string) => Promise<void>) | null>(null);

  // Update the ref with current implementation (captures current closure)
  initializeSessionRef.current = async (tenant: string, configId?: string) => {
    try {
      safeSetLoading(true);
      safeSetError(null);

      // Try to restore existing session first
      const restored = restoreSession();

      if (restored && restored.sessionId) {
        try {
          const stateResponse = await quizService.getSessionState(restored.sessionId);

          if (stateResponse.success && stateResponse.data) {
            safeSetSessionId(restored.sessionId);
            safeSetSessionState(stateResponse.data.sessionState);
            safeSetCurrentQuestion(stateResponse.data.currentQuestion || null);
            safeSetAnswers(restored.answers);
            safeSetLoading(false);
            return;
          }
        } catch (restoreError) {
          console.warn('[initializeSession] Could not restore session, starting new one:', restoreError);
          clearSession();
        }
      }

      // Start new session
      if (!configId) {
        throw new Error('Quiz configuration ID is required to start a session');
      }

      const response = await quizService.startQuizSession({
        quizId: configId,
      });

      if (!response.success || !response.data) {
        throw response.error || new Error('Failed to start quiz session');
      }

      const actualData = response.data;

      const responseSessionId = actualData?.sessionId;
      const responseQuizId = actualData?.quizId;
      const responseStatus = actualData?.status;
      const responseQuestion = actualData?.currentQuestion;
      const responseProgress = actualData?.progress;
      const responseStartedAt = actualData?.startedAt;

      // Validate required fields
      if (!responseSessionId) {
        throw new Error('Missing sessionId in response');
      }
      if (!responseQuestion) {
        throw new Error('Missing currentQuestion in response');
      }
      if (!responseProgress) {
        throw new Error('Missing progress in response');
      }

      // Build SessionState
      let sessionState: SessionState;
      try {
        sessionState = {
          sessionId: responseSessionId,
          quizId: responseQuizId,
          tenantId: tenant,
          status: responseStatus as SessionState['status'],
          currentQuestionIndex: responseProgress?.current ?? 0,
          answers: {},
          startedAt: responseStartedAt,
          lastActivityAt: responseStartedAt,
          progressPercentage: responseProgress?.percentage ?? 0,
        };
      } catch (sessionStateError) {
        console.error('[initializeSession] Failed during sessionState creation:', sessionStateError);
        throw sessionStateError;
      }

      // Check mounted status before proceeding
      if (!isMountedRef.current) {
        return;
      }

      // Transform Question into CurrentQuestion (quiz-engine format)
      const currentQuestion = transformQuestionDtoToCurrentQuestion(responseQuestion);

      safeSetSessionId(responseSessionId);
      safeSetSessionState(sessionState);
      safeSetCurrentQuestion(currentQuestion);
      safeSetAnswers({});
      safeSetPreviousQuestions(() => []);

      // Persist new session
      persistSession({
        sessionId: responseSessionId,
        tenantId: tenant,
        currentQuestionIndex: responseQuestion.questionNumber,
        answers: {},
        timestamp: Date.now(),
      });

      safeSetLoading(false);
    } catch (err) {
      console.error('[initializeSession] CAUGHT ERROR:', err);
      safeSetError(toApiError(err));
      safeSetLoading(false);
    }
  };

  // Stable wrapper that never changes - EMPTY DEPENDENCY ARRAY
  const initializeSession = useCallback(
    async (tenant: string, configId?: string): Promise<void> => {
      if (initializeSessionRef.current) {
        return initializeSessionRef.current(tenant, configId);
      }
      console.error('[initializeSession WRAPPER] ref.current is null!');
      return Promise.resolve();
    },
    [] // NO DEPENDENCIES - this wrapper never changes!
  );

  /**
   * Submit answer for current question
   */
  const submitAnswer = useCallback(
    async (questionKey: string, answer: AnswerValue) => {
      if (!sessionId) {
        safeSetError({
          code: 'NO_SESSION',
          message: 'No active quiz session',
        });
        return;
      }

      try {
        safeSetLoading(true);
        safeSetError(null);

        const response = await quizService.submitAnswer(sessionId, {
          questionKey,
          answer,
        });

        if (!response.success || !response.data) {
          throw response.error || new Error('Failed to submit answer');
        }

        const result = response.data;

        // Update answers
        const newAnswers = {
          ...answers,
          [questionKey]: answer,
        };
        safeSetAnswers(newAnswers);

        // Update session state
        safeSetSessionState(result.sessionState);

        if (result.isQuizComplete) {
          // Quiz completed - clear session
          clearSession();
        } else if (result.nextQuestion) {
          try {
            // Move to next question
            if (currentQuestion) {
              safeSetPreviousQuestions((prev) => [...prev, currentQuestion]);
            }

            // Transform nextQuestion from backend format to quiz-engine format
            const transformedNextQuestion = transformQuestionDtoToCurrentQuestion(result.nextQuestion);

            safeSetCurrentQuestion(transformedNextQuestion);

            // Persist updated session
            persistSession({
              currentQuestionIndex: transformedNextQuestion.questionNumber,
              answers: newAnswers,
            });
          } catch (transformError) {
            console.error('[QuizContext] Transform ERROR:', transformError);
            throw transformError; // Re-throw to be caught by outer catch
          }
        } else {
          console.warn('[QuizContext] Unexpected state: success but no nextQuestion and not complete!', {
            success: result.success,
            isQuizComplete: result.isQuizComplete,
            hasNextQuestion: !!result.nextQuestion,
            resultKeys: Object.keys(result),
          });
        }

        safeSetLoading(false);
      } catch (err) {
        safeSetError(toApiError(err));
        safeSetLoading(false);
      }
    },
    [sessionId, answers, currentQuestion, persistSession, clearSession]
  );

  /**
   * Navigate to previous question
   */
  const navigateBack = useCallback(async () => {
    if (!sessionId || previousQuestions.length === 0) {
      return;
    }

    try {
      safeSetLoading(true);
      safeSetError(null);

      const response = await quizService.getPreviousQuestion(sessionId);

      if (!response.success || !response.data) {
        throw response.error || new Error('Failed to navigate back');
      }

      // Update current question and remove from history
      safeSetCurrentQuestion(response.data);
      safeSetPreviousQuestions((prev) => prev.slice(0, -1));

      // Persist updated session
      persistSession({
        currentQuestionIndex: response.data.questionNumber,
      });

      safeSetLoading(false);
    } catch (err) {
      safeSetError(toApiError(err));
      safeSetLoading(false);
    }
  }, [sessionId, previousQuestions, persistSession]);

  /**
   * Abandon current session
   */
  const abandonSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      await quizService.abandonSession(sessionId);
      clearSession();
      safeSetSessionId(null);
      safeSetSessionState(null);
      safeSetCurrentQuestion(null);
      safeSetPreviousQuestions(() => []);
      safeSetAnswers({});
    } catch (err) {
      // Ignore errors on abandon
      console.error('Error abandoning session:', err);
    }
  }, [sessionId, clearSession]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Auto-initialize on mount if enabled
   */
  useEffect(() => {
    if (autoInitialize && tenantId) {
      void initializeSession(tenantId, quizConfigId).catch((err) => {
        // Error is handled internally in initializeSession
        console.error('Failed to auto-initialize session:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initializeSession excluded to prevent dependency loop causing Oe.current crash
  }, [autoInitialize, tenantId, quizConfigId]);

  /**
   * Context value
   */
  const contextValue: QuizContextValue = {
    sessionId,
    tenantId,
    sessionState,
    currentQuestion,
    previousQuestions,
    answers,
    progress,
    loading,
    error,
    initializeSession,
    submitAnswer,
    navigateBack,
    clearError,
    abandonSession,
  };

  return <QuizContext.Provider value={contextValue}>{children}</QuizContext.Provider>;
};

/**
 * Custom hook to use quiz context
 */
/** @deprecated Use individual hooks from '@/hooks/quiz' instead */
export const useQuizContext = (): QuizContextValue => {
  const context = useContext(QuizContext);

  if (!context) {
    throw new Error('useQuizContext must be used within QuizProvider');
  }

  // DEFENSIVE: Guarantee that progress exists even during initialization
  // This prevents race conditions where components access progress before it's set
  if (!context.progress) {
    console.warn('[useQuizContext] Progress is undefined, returning safe defaults');
    return {
      ...context,
      progress: {
        currentQuestionIndex: 0,
        totalQuestions: 0,
        completedQuestions: 0,
        percentComplete: 0,
      },
    };
  }

  return context;
};
